import { memo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { DEFAULT_DURATION_PRESETS } from 'root/src/shared/giveaways/giveaways.constants';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { RedesignStepper } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/RedesignStepper';
const DAY = 1000 * 60 * 60 * 24;
const HOUR = 1000 * 60 * 60;
const MINUTE = 1000 * 60;
const shortLabel = (label) => label
    .replace(/\sminutes?$/, 'm')
    .replace(/\shours?$/, 'h')
    .replace(/\sdays?$/, 'd');
const toParts = (ms) => ({
    days: Math.floor(ms / DAY),
    hours: Math.floor(ms / HOUR) % 24,
    minutes: Math.floor(ms / MINUTE) % 60,
});
const CUSTOM_UNITS = [
    { key: 'days', label: 'Days', max: 30 },
    { key: 'hours', label: 'Hours', max: 23 },
    { key: 'minutes', label: 'Minutes', max: 59 },
];
// Fresh redesign duration picker: a segmented preset bar with an animated sliding indicator
// (Framer layoutId) and an inline, animated "Custom" panel (days / hours / minutes steppers),
// replacing the legacy preset buttons + "Set custom" popover.
export const RedesignTimeDurationField = memo(() => {
    const form = useFormContext();
    const [customOpen, setCustomOpen] = useState(false);
    const reduceMotion = useReducedMotion();
    return (<Controller name="durationTime" control={form.control} render={({ field, fieldState }) => {
            const isPreset = DEFAULT_DURATION_PRESETS.some(preset => preset.value === field.value);
            const showCustom = customOpen || !isPreset;
            const parts = toParts(field.value || 0);
            const setPart = (key, value) => {
                const next = Object.assign(Object.assign({}, parts), { [key]: value });
                field.onChange(next.days * DAY + next.hours * HOUR + next.minutes * MINUTE);
            };
            const segments = [
                ...DEFAULT_DURATION_PRESETS.map(preset => ({
                    id: String(preset.value),
                    label: shortLabel(preset.label),
                    active: !showCustom && field.value === preset.value,
                    onClick: () => { field.onChange(preset.value); setCustomOpen(false); },
                })),
                { id: 'custom', label: 'Custom', active: showCustom, onClick: () => setCustomOpen(true) },
            ];
            return (<div className="w-full">
						<p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Duration</p>

						<div className="flex flex-wrap items-center gap-1 rounded-xl bg-white/[0.04] p-1">
							{segments.map(segment => (<button key={segment.id} type="button" onClick={segment.onClick} className={cn('relative flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors', segment.active ? 'text-white' : 'text-muted-foreground hover:text-foreground')}>
									{segment.active && (<motion.span layoutId="giveawayDurationIndicator" className="absolute inset-0 rounded-lg bg-primary" transition={reduceMotion ? { duration: 0 } : { type: 'spring', bounce: 0.2, duration: 0.4 }}/>)}
									<span className="relative z-10">{segment.label}</span>
								</button>))}
						</div>

						<AnimatePresence initial={false}>
							{showCustom && (<motion.div key="custom-panel" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' }} className="overflow-hidden">
									<div className="mt-3 flex flex-wrap items-center justify-center gap-6 rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] p-4">
										{CUSTOM_UNITS.map(({ key, label, max }) => (<div key={key} className="flex flex-col items-center gap-2">
												<span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
												<RedesignStepper min={0} max={max} value={parts[key]} onChange={value => setPart(key, value)}/>
											</div>))}
									</div>
								</motion.div>)}
						</AnimatePresence>

						{fieldState.error && <p className="mt-2 text-sm text-pink">{fieldState.error.message}</p>}
					</div>);
        }}/>);
});
